import { Icon } from "@iconify/react";
import { CtyButton } from "../button/CtyButton";
import { Badge, Button, Popover, PopoverContent, PopoverTrigger } from '@nextui-org/react';
import { useEffect } from "react";
import { useCartOperations, CartDownloadType } from "./useCartOperations";
import { useHoverMenu } from "./useHoverMenu";

interface CartItemsListProps {
    openCart?: boolean;
    popupPlacement?: any;
}

const CartItemsList = (props: CartItemsListProps) => {
    const { openCart = false, popupPlacement = 'bottom' } = props;

    const { isOpen: isCartOpen, setIsOpen: setIsCartOpen, open: openCartMenu, closeSoon: closeCartSoon, close: closeCart } = useHoverMenu();

    useEffect(() => {
        if (openCart) {
            openCartMenu();
        }
    }, [openCart, openCartMenu]);

    const {
        itemsArray,
        itemCount,
        placeOrder,
        setPlaceOrder,
        isFetchingCartCsvFile,
        isPlacingOrder,
        incrementQuantity,
        decrementQuantity,
        handleDelete,
        handleDownload,
    } = useCartOperations();

    return (
        <Popover placement={popupPlacement} isOpen={isCartOpen} onOpenChange={setIsCartOpen} shouldBlockScroll={false}>
            <Badge
                color="danger"
                content={itemCount}
                size="md"
                isInvisible={itemCount === 0}
            >
                <PopoverTrigger>
                    <Button
                        id="cart-list-btn"
                        isIconOnly
                        radius="full"
                        size="sm"
                        variant="light"
                        disableAnimation
                        className="h-10 w-10 min-w-10 hover:bg-[#F4F4F5] aria-expanded:!scale-100 aria-expanded:!opacity-100 data-[pressed=true]:!scale-100 dark:hover:bg-content2"
                        onMouseEnter={openCartMenu}
                        onMouseLeave={closeCartSoon}
                    >
                        <Icon icon="lucide:shopping-cart" width={17} height={17} className="text-[#181818] dark:text-[var(--cty-text-secondary)]" />
                    </Button>
                </PopoverTrigger>
            </Badge>
            <PopoverContent className="cty-header-menu w-[200px] bg-content1" onMouseEnter={openCartMenu} onMouseLeave={closeCartSoon}>
                <div className="flex w-full flex-col">
                    <div className="border-b p-3 text-[16px] font-semibold">My Cart</div>
                    <div aria-label="carts-list" className={`w-full ${itemCount > 3 ? "max-h-[300px] overflow-y-auto" : ""}`}>
                        {itemsArray.map(([key, item]) => (
                            <div key={key} className="p-0 m-0 border-b">
                                <div className="flex flex-row justify-between items-start p-2 ">
                                    <div className="flex-1 flex flex-col gap-1">
                                        <div>{item.partName}</div>
                                        <div>Code: <span className="font-light text-xs">{item.partNumber}</span></div>
                                        <div className="flex items-center gap-1">
                                            <CtyButton isIconOnly size="sm" variant="light" onPress={() => decrementQuantity(item)}>
                                                <Icon icon="tabler:minus" width="16" height="16" className="text-primary" />
                                            </CtyButton>
                                            <span className="text-primary font-semibold w-[20px] bg-[#F5F5F5] flex justify-center">
                                                {item.totalQuanity}
                                            </span>
                                            <CtyButton isIconOnly size="sm" variant="light" onPress={() => incrementQuantity(item)}>
                                                <Icon icon="tabler:plus" width="16" height="16" className="text-primary" />
                                            </CtyButton>
                                        </div>
                                    </div>
                                    <Button
                                        id={`${key}-delete-btn`}
                                        isIconOnly
                                        radius="sm"
                                        size="sm"
                                        variant="light"
                                        onPress={() => {
                                            handleDelete(item);
                                            closeCart();
                                        }}
                                    >
                                        <Icon icon="hugeicons:delete-02" width="1.1rem" height="1.1rem" />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                    {!placeOrder &&
                        <CtyButton className="my-2" variant="bordered" radius="sm" size="lg" onPress={() => setPlaceOrder(true)} isDisabled={itemCount === 0}>
                            Place Order
                        </CtyButton>
                    }
                    {placeOrder &&
                        <>
                            <CtyButton className="my-2" isLoading={isFetchingCartCsvFile} variant="bordered" radius="sm" size="lg" onPress={() => { handleDownload(CartDownloadType.CSV); closeCart(); }}>
                                Download CSV
                            </CtyButton>
                            <CtyButton className="my-2" isLoading={isPlacingOrder} variant="bordered" color={'primary'} radius="sm" size="lg" onPress={() => { handleDownload(CartDownloadType.JSON); closeCart(); }}>
                                Download JSON
                            </CtyButton>
                        </>
                    }
                </div>
            </PopoverContent>
        </Popover>
    );
};


export { CartItemsList };
